import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, PageHeader, StatCard, TableSkeleton, Modal } from '../../components/ui';
import { Toast } from '../../components/ui/Alert';
import { NewRequestForm, RaisedResult } from './NewRequestForm';
import {
	EmailWorkflowApi,
	BoardFilters,
	BoardResponse,
	BoardView,
	WorkflowScope,
	WorkflowSummary,
} from '../../services/emailWorkflowApi';
import { CITY_OPTIONS } from './badges';
import { WorkflowTable } from './WorkflowTable';

/**
 * Page 1 — the requester board (Phase 1 of docs/plan-approvals-restructure.md).
 * One list, no tabs: everyone sees the requests THEY raised (view=raised).
 * Users whose /me scope descriptor is non-empty (approvers/finance/leadership)
 * additionally get a "My requests | All in my scope" toggle — the server widens
 * view=all to their descriptor scope; the UI never checks user types.
 * Filtering is by the Approval-status + Finance dropdowns (tabs are gone).
 */

const PAGE_SIZE = 50;

// Default date window = the current month (industry default: never an unbounded
// list). "Reset filters" returns here, not to an empty filter set.
const pad = (n: number) => String(n).padStart(2, '0');
const defaultDates = (): { from: string; to: string } => {
	const d = new Date();
	return {
		from: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`,
		to: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
	};
};

export const EmailWorkflowBoard: React.FC = () => {
	const navigate = useNavigate();
	// scope descriptor (contract v2) — null until loaded / on old backends without /me.
	const [scope, setScope] = useState<WorkflowScope | null>(null);
	const [showAll, setShowAll] = useState(false);
	const view: BoardView = showAll ? 'all' : 'raised';
	const [filters, setFilters] = useState<Omit<BoardFilters, 'view' | 'page' | 'page_size'>>(defaultDates());
	const [page, setPage] = useState(1);
	const [board, setBoard] = useState<BoardResponse | null>(null);
	const [summary, setSummary] = useState<WorkflowSummary | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	// accordion: the one expanded row's thread id (row overview → inline detail).
	const [openThreadId, setOpenThreadId] = useState<string | null>(null);
	// New Request modal — also opened by the /email-workflows/new deep-link.
	const location = useLocation();
	const isNewRoute = location.pathname.endsWith('/new');
	const [newOpen, setNewOpen] = useState(false);
	const [toast, setToast] = useState<string | null>(null);
	// mobile: the filter grid hides behind a "Filters" button (search stays visible)
	const [filtersOpen, setFiltersOpen] = useState(false);

	const closeNewModal = () => {
		setNewOpen(false);
		if (isNewRoute) navigate('/email-workflows', { replace: true });
	};

	const handleRaised = (r: RaisedResult) => {
		closeNewModal();
		const role = r.routing?.current_tier_role;
		const tierLabel = role === 'tier1' ? 'Approver' : role === 'tier2' ? 'Escalation' : role === 'accounts' ? 'Accounts' : 'the first approver';
		setToast(`Request raised — sent to ${tierLabel}.`);
		setShowAll(false); // land on "My requests" so the new row is right there
		setPage(1);
		setOpenThreadId(r.thread_id); // land on My Requests with the new row expanded
		setReloadKey(k => k + 1);
	};

	// summary once (not per view/page)
	useEffect(() => {
		EmailWorkflowApi.getSummary().then(setSummary).catch(() => setSummary(null));
	}, [reloadKey]);

	// scope descriptor once — a failure (old backend, no /me) just hides the toggle.
	useEffect(() => {
		EmailWorkflowApi.getMe().then(setScope).catch(() => setScope(null));
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		// Ask the backend to filter from/to by the approval-requested date (first email).
		// Harmless before the backend implements it (unknown param ignored → filters
		// created_at as today); takes effect once request_date + date_field ship.
		const dateFieldParam =
			filters.from || filters.to ? { date_field: 'request_date' as const } : {};
		EmailWorkflowApi.getBoard({ ...filters, ...dateFieldParam, view, page, page_size: PAGE_SIZE })
			.then(res => {
				if (!cancelled) setBoard(res);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setError((err as { message?: string })?.message || 'Could not load the board.');
					setBoard(null);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [view, filters, page, reloadKey]);

	const rows = board?.rows ?? [];
	const total = board?.total ?? 0;
	// backend ships request_date (approval-requested = first email date) later; adapt when present.
	const hasRequestDate = rows.some(r => r.request_date != null);
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const cards = useMemo(() => {
		const a = summary?.by_approval_status ?? {};
		const f = summary?.by_finance_status ?? {};
		return {
			pending: a.pending ?? 0,
			approved: a.approved ?? 0,
			paid: f.paid ?? 0,
			recheck: f.recheck ?? 0,
		};
	}, [summary]);

	const setFilter = (patch: Partial<typeof filters>) => {
		setPage(1);
		setFilters(prev => {
			const next = { ...prev, ...patch };
			// strip empty values so we don't send blank params
			Object.keys(next).forEach(k => {
				const v = (next as Record<string, unknown>)[k];
				if (v === '' || v === undefined) delete (next as Record<string, unknown>)[k];
			});
			return next;
		});
	};

	const dd = defaultDates();
	const activeFilterCount = Object.entries(filters).filter(
		([k, v]) => !(k === 'from' && v === dd.from) && !(k === 'to' && v === dd.to)
	).length;
	const cardDefs = [
		['⏳', 'Pending approval', cards.pending, 'orange', { approval_status: 'pending' }],
		['✅', 'Approved', cards.approved, 'green', { approval_status: 'approved' }],
		['💸', 'Paid', cards.paid, 'blue', { finance_status: 'paid' }],
		['🔁', 'Needs recheck', cards.recheck, 'yellow', { finance_status: 'recheck' }],
	] as const;
	const cardState = (patch: Record<string, string>) => {
		const [k, v] = Object.entries(patch)[0] as [keyof typeof filters, string];
		return { k, v, active: filters[k] === v };
	};

	return (
		<div className="p-4 sm:p-6">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<PageHeader title="Approval Board" totalItems={total} itemType="requests" icon="🗂️" />
				<button
					type="button"
					onClick={() => setNewOpen(true)}
					className="w-full sm:w-auto shrink-0 px-4 py-2.5 sm:py-2 rounded-md bg-primary text-white text-sm font-medium"
				>
					＋ New Request
				</button>
			</div>

			{/* mobile (<sm): compact chip strip — same counts, same one-tap filtering */}
			<div className="sm:hidden flex gap-2 overflow-x-auto pb-1 mt-3 mb-3">
				{cardDefs.map(([, label, value, , patch]) => {
					const { k, v, active } = cardState(patch as Record<string, string>);
					return (
						<button
							key={label}
							type="button"
							aria-pressed={active}
							onClick={() => setFilter({ [k]: active ? undefined : v } as Partial<typeof filters>)}
							className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
								active ? 'bg-primary/10 border-primary text-primary' : 'bg-white border-gray-200 text-gray-600'
							}`}
						>
							{label}
							<span
								className={`px-1.5 py-0.5 rounded-full text-[11px] leading-none tabular-nums ${
									active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
								}`}
							>
								{value}
							</span>
						</button>
					);
				})}
			</div>

			{/* desktop (sm+): summary cards — each is also a one-click filter on the list below */}
			<div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
				{cardDefs.map(([icon, label, value, color, patch]) => {
					const { k, v, active } = cardState(patch as Record<string, string>);
					return (
						<button
							key={label}
							type="button"
							aria-pressed={active}
							title={active ? 'Click to remove this filter' : `Show only: ${label}`}
							onClick={() => setFilter({ [k]: active ? undefined : v } as Partial<typeof filters>)}
							className={`text-left rounded-lg transition-shadow ${active ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
						>
							<StatCard icon={icon} label={label} value={value} color={color} />
						</button>
					);
				})}
			</div>
			<div className="mb-6" />

			{/* scope toggle — only for viewers whose /me descriptor carries scope */}
			{scope != null && (scope.cities === 'all' || scope.cities.length > 0) && (
				<div
					className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
					role="group"
					aria-label="Board scope"
				>
					{([
						[false, 'My requests'],
						[true, 'All in my scope'],
					] as const).map(([all, label]) => (
						<button
							key={label}
							type="button"
							onClick={() => {
								setShowAll(all);
								setPage(1);
							}}
							aria-pressed={showAll === all}
							className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
								showAll === all ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
							}`}
						>
							{label}
						</button>
					))}
				</div>
			)}

			{/* filters — search always visible; the rest hides behind "Filters" on mobile */}
			<Card className="mb-4">
				<div className="flex gap-2">
					<input
						type="search"
						placeholder="Search gist / counterparty…"
						defaultValue={filters.q ?? ''}
						onKeyDown={e => {
							if (e.key === 'Enter') setFilter({ q: (e.target as HTMLInputElement).value });
						}}
						onBlur={e => setFilter({ q: e.target.value })}
						className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm"
					/>
					<button
						type="button"
						onClick={() => setFiltersOpen(o => !o)}
						aria-expanded={filtersOpen}
						className="sm:hidden shrink-0 inline-flex items-center gap-1.5 border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-600"
					>
						Filters
						{activeFilterCount > 0 && (
							<span className="bg-primary text-white rounded-full px-1.5 py-0.5 text-[11px] leading-none tabular-nums">
								{activeFilterCount}
							</span>
						)}
					</button>
				</div>
				<div className={`${filtersOpen ? 'grid' : 'hidden'} sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3`}>
					<select
						value={filters.city_id ?? ''}
						onChange={e => setFilter({ city_id: e.target.value ? Number(e.target.value) : undefined })}
						className="border border-gray-200 rounded-md px-3 py-1.5 text-sm"
					>
						{CITY_OPTIONS.map(c => (
							<option key={String(c.value)} value={c.value}>
								{c.label}
							</option>
						))}
					</select>
					<select
						value={filters.workflow_type ?? ''}
						onChange={e => setFilter({ workflow_type: (e.target.value || undefined) as never })}
						className="border border-gray-200 rounded-md px-3 py-1.5 text-sm"
					>
						<option value="">All types</option>
						<option value="vendor_payment">Vendor payment</option>
						<option value="reimbursement">Reimbursement</option>
						<option value="ad_hoc_manpower">Ad-hoc manpower</option>
						<option value="unknown">Unknown</option>
					</select>
					<select
						aria-label="Approval status"
						value={filters.approval_status ?? ''}
						onChange={e => setFilter({ approval_status: (e.target.value || undefined) as never })}
						className="border border-gray-200 rounded-md px-3 py-1.5 text-sm"
					>
						<option value="">All approval states</option>
						<option value="pending">Pending</option>
						<option value="approved">Approved</option>
						<option value="rejected">Rejected</option>
					</select>
					<select
						aria-label="Finance status"
						value={filters.finance_status ?? ''}
						onChange={e => setFilter({ finance_status: (e.target.value || undefined) as never })}
						className="border border-gray-200 rounded-md px-3 py-1.5 text-sm"
					>
						<option value="">All finance states</option>
						<option value="awaiting">Awaiting</option>
						<option value="paid">Paid</option>
						<option value="proof_attached">Proof attached</option>
						<option value="recheck">Recheck</option>
						<option value="done">Done</option>
					</select>
					<div className="flex items-center gap-2">
						<label
							className="text-xs text-gray-500 whitespace-nowrap"
							title={
								hasRequestDate
									? 'Filters by when approval was requested (first email date)'
									: 'Filters by when the request was added to the dashboard (not the email date)'
							}
						>
							{hasRequestDate ? 'Requested from' : 'Added from'}
						</label>
						<input
							type="date"
							value={filters.from ?? ''}
							onChange={e => setFilter({ from: e.target.value })}
							className="border border-gray-200 rounded-md px-2 py-1.5 text-sm flex-1"
						/>
					</div>
					<div className="flex items-center gap-2">
						<label className="text-xs text-gray-500">to</label>
						<input
							type="date"
							value={filters.to ?? ''}
							onChange={e => setFilter({ to: e.target.value })}
							className="border border-gray-200 rounded-md px-2 py-1.5 text-sm flex-1"
						/>
					</div>
					<label className="flex items-center gap-2 text-sm text-gray-600">
						<input
							type="checkbox"
							checked={filters.needs_review === true}
							onChange={e => setFilter({ needs_review: e.target.checked ? true : undefined })}
						/>
						Needs review only
					</label>
					<button
						type="button"
						onClick={() => {
							setPage(1);
							setFilters(defaultDates());
						}}
						className="text-sm text-primary underline justify-self-start"
					>
						Reset filters
					</button>
				</div>
			</Card>

			{/* skeleton only on FIRST load — refetches keep the table (and any expanded
			    detail row with its "Approved — …" message) mounted instead of flashing */}
			{loading && !board && <TableSkeleton />}

			{!loading && error && (
				<Card className="text-center py-10">
					<p className="text-red-600 font-medium">{error}</p>
					<button
						type="button"
						onClick={() => setReloadKey(k => k + 1)}
						className="mt-3 text-sm text-primary underline"
					>
						Try again
					</button>
				</Card>
			)}

			{!loading && !error && rows.length === 0 && (
				<Card className="text-center py-12">
					<div className="text-4xl mb-3">📭</div>
					<p className="text-gray-800 font-medium">
						{showAll
							? 'No requests in your scope'
							: 'No requests match these filters'}
					</p>
					<p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
						{showAll
							? 'Nothing in your cities/facilities matches the filters (dates default to this month).'
							: 'The dates default to this month — widen them to see older requests, or use ＋ New Request to raise one.'}
					</p>
				</Card>
			)}

			{!error && rows.length > 0 && (
				<Card className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
					<WorkflowTable
						rows={rows}
						openThreadId={openThreadId}
						onToggle={setOpenThreadId}
						onRowChanged={msg => {
							setReloadKey(k => k + 1);
							if (msg) setToast(msg); // the acted row may leave this list — keep the confirmation
						}}
						startIndex={(page - 1) * PAGE_SIZE}
					/>

					{/* pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-between mt-4 text-sm text-gray-600">
							<span>
								Page {page} of {totalPages} · {total} total
							</span>
							<div className="flex gap-2">
								<button
									type="button"
									disabled={page <= 1}
									onClick={() => setPage(p => p - 1)}
									className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40"
								>
									Prev
								</button>
								<button
									type="button"
									disabled={page >= totalPages}
									onClick={() => setPage(p => p + 1)}
									className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40"
								>
									Next
								</button>
							</div>
						</div>
					)}
				</Card>
			)}

			{/* New Request — modal over the board; /email-workflows/new deep-links here */}
			<Modal open={newOpen || isNewRoute} onClose={closeNewModal} title="New payment request" size="md">
				<NewRequestForm onRaised={handleRaised} />
			</Modal>

			{toast && (
				<Toast variant="success" position="top-center" duration={5000} onClose={() => setToast(null)}>
					{toast}
				</Toast>
			)}
		</div>
	);
};

export default EmailWorkflowBoard;
